import React, { useState, useEffect } from 'react';
import { db } from '../services/db';
import { ClientEntity, GlobalSettings, Payment } from '../types';
import { Loader2, CreditCard, UploadCloud, CheckCircle2, Lock, ArrowRight, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';

/**
 * Chave PUBLICA do Stripe (`pk_...`), a unica que pode viver no navegador — ela
 * so cria sessao de checkout, nunca move dinheiro. A secreta (`sk_...`) fica na
 * API, e ja esteve como fallback em `server.ts:73`.
 *
 * Sem fallback, mesmo sendo publica. `loadStripe('pk_test_a_sua_chave_...')`
 * fazia o checkout falhar dentro do widget do Stripe, com uma mensagem que nao
 * diz que falta variavel de build. `null` desliga o checkout de forma explicita,
 * e a tela avisa.
 */
const chavePublicaStripe = import.meta.env.VITE_STRIPE_PUBLIC_KEY ?? '';
const stripePromise = chavePublicaStripe ? loadStripe(chavePublicaStripe) : null;

interface ClientPaymentBoardProps {
  client: ClientEntity;
  onLogout: () => void;
  onPaymentSuccess?: () => void;
  embedded?: boolean;
}

export const ClientPaymentBoard: React.FC<ClientPaymentBoardProps> = ({ client, onLogout, onPaymentSuccess, embedded = false }) => {
  const [globalSettings, setGlobalSettings] = useState<GlobalSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [generatingStripe, setGeneratingStripe] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  
  // PIX options
  const [showPixManual, setShowPixManual] = useState(false);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [pixStatus, setPixStatus] = useState<'upload' | 'verifying' | 'failed' | 'paid'>('upload');
  
  // Checking for existing pending manual payments
  const [existingPayment, setExistingPayment] = useState<Payment | null>(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [settings, payments] = await Promise.all([
        db.getGlobalSettings(),
        db.getPaymentsByClient(client.id)
      ]);
      setGlobalSettings(settings);

      // Check if there's a payment under verification
      const verifyingPayment = payments.find(p => p.status === 'verifying');
      if (verifyingPayment) {
        setExistingPayment(verifyingPayment);
        setPixStatus('verifying');
      }

    } catch (err) {
      toast.error('Erro ao carregar dados do pagamento.');
    } finally {
      setLoading(false);
    }
  };

  const currentPrice = client.customPrice !== null && client.customPrice !== undefined 
    ? client.customPrice 
    : (globalSettings?.defaultSitePrice || 99.90);

  const handleStripeCheckout = async () => {
    setGeneratingStripe(true);
    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId: client.id }),
      });

      let data;
      try {
        const text = await response.text();
        data = JSON.parse(text);
      } catch (e) {
        throw new Error('Erro ao comunicar com o servidor. A resposta não parece ser válida.');
      }
      
      if (!response.ok) {
        throw new Error(data.error || 'Erro ao gerar o link');
      }

      setStripeClientSecret(data.clientSecret);
      setGeneratingStripe(false);
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || 'Falha ao processar pagamento.');
      setGeneratingStripe(false);
    }
  };

  const handleReceiptUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setReceiptFile(e.target.files[0]);
    }
  };

  const submitReceipt = async () => {
    if (!receiptFile) {
      toast.error("Anexe um comprovante antes de enviar.");
      return;
    }

    setUploadingReceipt(true);
    try {
      // Create a blob URL to simulate uploading since we are not using Supabase Storage in this demo,
      // wait, the prompt says "vai para o Supabase Storage". But since we aren't using real auth/storage in the frontend easily without config,
      // I will simulate uploading a receipt or you can make a generic API.
      // For the AI Studio sandbox, we'll convert it to base64 or a dummy URL for the demonstration.
      
      // Faz o upload da imagem pro Supabase Storage
      const receiptUrl = await db.uploadAsset(receiptFile);
      
      // Salva o pagamento no banco de dados com a URL real do comprovante
      await db.createPayment({
        clientId: client.id,
        amount: currentPrice,
        paymentMethod: 'pix_manual',
        status: 'verifying',
        receiptUrl: receiptUrl
      });

      toast.success("Comprovante salvo com sucesso e enviado para análise!");
      setPixStatus('verifying');
    } catch (err) {
      console.error(err);
      toast.error('Erro ao enviar o comprovante.');
    } finally {
      setUploadingReceipt(false);
    }
  };

  if (loading) {
    return (
      <div className={embedded ? "flex flex-col items-center justify-center py-20" : "min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center"}>
        <Loader2 className="animate-spin text-black" size={40} />
        <p className="mt-4 text-gray-500 font-medium tracking-wide">Carregando informações...</p>
      </div>
    );
  }

  // Se o pagamento manual estiver em análise
  if (pixStatus === 'verifying') {
    return (
      <div className={embedded ? "flex flex-col items-center justify-center p-6" : "min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center p-6"}>
        <div className="bg-white max-w-md w-full p-8 rounded-3xl border border-gray-200 shadow-xl text-center">
          <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <Loader2 className="text-amber-500 animate-spin" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Comprovante em Análise</h2>
          <p className="text-gray-500 mb-8 leading-relaxed">
            Recebemos o seu comprovante PIX. Nossa equipe está verificando e o seu site será liberado em até algumas horas.
          </p>
          {!embedded && (
            <button 
              onClick={onLogout}
              className="w-full flex justify-center items-center gap-2 text-gray-600 hover:text-black font-medium transition-colors"
            >
              <LogOut size={18} /> Sair
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? "flex flex-col" : "min-h-screen bg-[#FAFAFA] flex flex-col"}>
      {!embedded && (
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-black rounded-xl flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-xl leading-none">{client.data.name.charAt(0)}</span>
            </div>
            <div>
              <h2 className="font-semibold text-gray-900 leading-tight truncate">{client.data.name}</h2>
              <p className="text-xs text-gray-500 mt-0.5">Liberação de Site</p>
            </div>
          </div>
          <button 
            onClick={onLogout}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            <LogOut size={16} /> Sair
          </button>
        </header>
      )}

      <main className={`flex-1 overflow-y-auto flex flex-col items-center justify-center ${embedded ? 'pt-0' : 'p-6'}`}>
        <div className="max-w-2xl w-full">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 text-blue-600 rounded-full mb-6 relative">
              <Lock size={32} />
              <div className="absolute top-0 right-0 w-4 h-4 bg-red-500 border-2 border-white rounded-full"></div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3 tracking-tight">Site Bloqueado</h1>
            <p className="text-lg text-gray-500">
              Seu projeto está pronto, mas o acesso ainda está bloqueado. Realize o pagamento para liberar todas as funcionalidades e ver o seu site online.
            </p>
          </div>

          <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <div>
                <p className="text-sm text-gray-500 uppercase tracking-wider font-semibold mb-1">Valor para Liberação</p>
                <h3 className="text-3xl font-bold text-gray-900">R$ {currentPrice.toFixed(2)}</h3>
              </div>
              <div className="bg-green-100 text-green-800 text-xs font-bold uppercase py-1.5 px-3 rounded-full">
                Pagamento Único
              </div>
            </div>

            <div className="p-8">
              {stripeClientSecret ? (
                <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">Finalizar Pagamento</h3>
                    <button 
                      onClick={() => setStripeClientSecret(null)}
                      className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
                    >
                      Voltar
                    </button>
                  </div>
                  {stripePromise ? (
                    <EmbeddedCheckoutProvider
                      stripe={stripePromise}
                      options={{clientSecret: stripeClientSecret}}
                    >
                      <EmbeddedCheckout />
                    </EmbeddedCheckoutProvider>
                  ) : (
                    /* Sem VITE_STRIPE_PUBLIC_KEY o widget do Stripe renderiza vazio
                       e nao diz por que. Falta de configuracao tem de aparecer como
                       falta de configuracao. */
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                      Checkout indisponivel: falta <code>VITE_STRIPE_PUBLIC_KEY</code> nas
                      variaveis de build deste painel. Use o PIX manual abaixo enquanto isso.
                    </div>
                  )}
                </div>
              ) : !showPixManual ? (
                <div className="space-y-4">
                  {/* Option 1: Stripe Checkout */}
                  <button 
                    onClick={handleStripeCheckout}
                    disabled={generatingStripe}
                    className="w-full relative group bg-black hover:bg-gray-900 text-white rounded-2xl p-5 flex items-center justify-between transition-all"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center shrink-0">
                        <CreditCard size={24} className="text-white" />
                      </div>
                      <div className="text-left">
                        <h4 className="font-semibold text-lg">Pagar agora (Cartão ou PIX)</h4>
                        <p className="text-sm text-gray-400">Liberação automatizada na hora via Stripe</p>
                      </div>
                    </div>
                    {generatingStripe ? (
                      <Loader2 className="animate-spin text-white" size={24} />
                    ) : (
                      <ArrowRight size={24} className="text-gray-400 group-hover:text-white transition-colors" />
                    )}
                  </button>

                  <div className="relative flex items-center py-4">
                    <div className="flex-grow border-t border-gray-200"></div>
                    <span className="flex-shrink-0 mx-4 text-gray-400 text-sm font-medium">Ou</span>
                    <div className="flex-grow border-t border-gray-200"></div>
                  </div>

                  {/* Option 2: Manual PIX */}
                  <button 
                    onClick={() => setShowPixManual(true)}
                    className="w-full relative group bg-white border-2 border-gray-200 hover:border-gray-900 text-gray-900 rounded-2xl p-5 flex items-center justify-between transition-all"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center shrink-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M11.6661 21.6429L1.92131 11.898C1.52809 11.5048 1.52809 10.8672 1.92131 10.474L11.6661 0.729177C12.0593 0.335965 12.6969 0.335965 13.0901 0.729177L22.8349 10.474C23.2281 10.8672 23.2281 11.5048 22.8349 11.898L13.0901 21.6429C12.6969 22.0361 12.0593 22.0361 11.6661 21.6429ZM19.2637 11.186L12.3781 4.30045C12.2743 4.19662 12.1059 4.19662 12.0021 4.30045L5.11655 11.186C5.01272 11.2898 5.01272 11.4582 5.11655 11.562L12.0021 18.4475C12.1059 18.5514 12.2743 18.5514 12.3781 18.4475L19.2637 11.562C19.3675 11.4582 19.3675 11.2898 19.2637 11.186Z" fill="currentColor"/>
                          <path d="M7.65283 11.1863L12.3782 6.46083C12.482 6.35701 12.6504 6.35701 12.7543 6.46083L17.4797 11.1863C17.5835 11.2901 17.5835 11.4585 17.4797 11.5623L12.7543 16.2878C12.6504 16.3916 12.482 16.3916 12.3782 16.2878L7.65283 11.5623C7.54901 11.4585 7.54901 11.2901 7.65283 11.1863Z" fill="currentColor"/>
                        </svg>
                      </div>
                      <div className="text-left">
                        <h4 className="font-semibold text-lg">Transferência PIX s/ taxa</h4>
                        <p className="text-sm text-gray-500">Enviar comprovante para liberação manual</p>
                      </div>
                    </div>
                    <ArrowRight size={24} className="text-gray-300 group-hover:text-gray-900 transition-colors" />
                  </button>
                </div>
              ) : (
                <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
                  <div className="bg-amber-50 p-6 rounded-2xl border border-amber-200">
                    <h4 className="font-semibold text-amber-900 mb-2">Dados para Transferência</h4>
                    <p className="text-amber-800 text-sm mb-4">
                      Realize um PIX direto para nossa conta e evite taxas.
                    </p>
                    <div className="flex justify-between items-center bg-white p-4 rounded-xl border border-amber-100">
                      <div>
                        <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Chave PIX (CNPJ)</span>
                        <div className="font-mono font-bold text-lg text-gray-900">{globalSettings?.pixCnpj || 'Não configurado'}</div>
                      </div>
                      <button 
                        onClick={() => {
                          if (globalSettings?.pixCnpj) {
                            navigator.clipboard.writeText(globalSettings.pixCnpj);
                            toast.success('Chave PIX copiada!');
                          }
                        }}
                        className="text-amber-600 hover:bg-amber-100 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                      >
                        Copiar
                      </button>
                    </div>
                    <div className="mt-4 flex justify-between items-center bg-white p-4 rounded-xl border border-amber-100">
                      <div>
                        <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Valor Exato</span>
                        <div className="font-mono font-bold text-lg text-gray-900">R$ {currentPrice.toFixed(2)}</div>
                      </div>
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(currentPrice.toFixed(2).toString());
                          toast.success('Valor copiado!');
                        }}
                        className="text-amber-600 hover:bg-amber-100 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                      >
                        Copiar
                      </button>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 mt-6">Anexar Comprovante</h4>
                    <p className="text-sm text-gray-500 mb-3">Tire um print ou salve o PDF do comprovante e anexe abaixo.</p>
                    
                    <label className={`block w-full border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${receiptFile ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-gray-400 bg-gray-50'}`}>
                      <input 
                        type="file" 
                        accept="image/*,.pdf" 
                        className="hidden" 
                        onChange={handleReceiptUpload}
                      />
                      {receiptFile ? (
                        <div className="flex flex-col items-center">
                          <CheckCircle2 size={32} className="text-green-500 mb-2" />
                          <p className="font-medium text-green-800">{receiptFile.name}</p>
                          <p className="text-xs text-green-600 mt-1">Clique para trocar</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          <UploadCloud size={32} className="text-gray-400 mb-2" />
                          <p className="font-medium text-gray-600">Arraste um arquivo ou clique para procurar</p>
                          <p className="text-xs text-gray-500 mt-1">PNG, JPG ou PDF</p>
                        </div>
                      )}
                    </label>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button 
                      onClick={() => setShowPixManual(false)}
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                    >
                      Voltar
                    </button>
                    <button 
                      onClick={submitReceipt}
                      disabled={!receiptFile || uploadingReceipt}
                      className="flex-1 bg-black hover:bg-gray-900 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:bg-gray-300"
                    >
                      {uploadingReceipt ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                      Enviar para Aprovação
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
